/*
 * Copyright (c) 2021 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import { Epic, ofType } from 'redux-observable';
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators';
import { EMPTY, from, NEVER } from 'rxjs';
import { RootState } from '../store.typings';
import {
    fetchAiActivitiesListSuccessActon,
    fetchCashRegistersSuccessAction,
    fetchCitiesListSuccessActon,
    fetchFirmwareVersionsListSuccessAction,
    fetchFiscalDriverVersionListSuccessAction,
    fetchLowerActivitiesListSuccessActon,
    fetchMainActivitiesListSuccessActon,
    fetchMiddleActivitiesListSuccessActon,
    fetchOperatorsListSuccessAction,
    fetchRegionsListSuccessActon,
    fetchStoreTypesSuccessActon,
    fetchTaxationTypesListSuccessAction,
    fetchTaxAuthoritiesListSuccessAction,
    fetchUsersListSuccessAction,
    ShortListsAction,
} from '../actions/AShortLists';
import { CashBoxesAction, CashBoxesActionType } from '../actions/CashBoxRegistry/cashBoxesActions';
import { StoresMapAction, StoresMapActionType } from '../actions/AStoresMap';
import { CompaniesAction, CompaniesActionType } from '../actions/companiesActions';
import { StoresActions, StoresActionType } from '../actions/storesActions';
import { ReportsActions, ReportsActionType } from '../actions/reportsActions';
import { CashRegistersAction, CashRegistersActionType } from '../actions/cashRegistersActions';
import { AlertsAction, AlertsActionType } from '../actions/alertsActions';
import { AlertsSettingsActionType } from '../actions/alertsSettingsActions';
import { AlertsDetailsActionType } from '../actions/alertDetailsActions';
import { SettingsUserInfoActionType } from '../actions/settingsUserInfoActions';
import { AdminUsersActionType } from '../actions/adminUsersActions';
import { UiType } from '../api/auth/authClient.typings';
import { DevicesActionType } from '../actions/devicesActions';
import { shortListsClient } from '../api/shortLists/shortListsClient';
import { ServiceActionType } from '../actions/serviceActions';

type ShortListsEpic = Epic<
    | ShortListsAction
    | CashBoxesAction
    | StoresMapAction
    | CompaniesAction
    | StoresActions
    | ReportsActions
    | CashRegistersAction
    | AlertsAction,
    | ShortListsAction
    | CashBoxesAction
    | StoresMapAction
    | CompaniesAction
    | StoresActions
    | ReportsActions
    | CashRegistersAction
    | AlertsAction,
    RootState
>;

export const fetchStoreTypesEpic: ShortListsEpic = (action$, state) => {
    return action$.pipe(
        ofType(
            CashBoxesActionType.OPEN_REGISTRY_CASHBOXES_LIST_PAGE,
            StoresMapActionType.OPEN_STORES_MAP_PAGE,
            CompaniesActionType.OPEN_REGISTRY_COMPANIES_LIST_PAGE,
            StoresActionType.OPEN_REGISTRY_STORES_LIST_PAGE,
            ReportsActionType.OPEN_REPORTS_PAGE,
        ),

        switchMap(() => {
            const { listStoreTypes } = state.value.shortListsReducer;

            if (!listStoreTypes || listStoreTypes.length === 0) {
                return from(shortListsClient.getStoreTypes()).pipe(
                    map((storeTypes) => fetchStoreTypesSuccessActon(storeTypes)),

                    catchError(() => NEVER),

                    takeUntil(
                        action$.ofType(
                            CashBoxesActionType.EXIT_REGISTRY_CASHBOXES_LIST_PAGE,
                            StoresMapActionType.EXIT_STORES_MAP_PAGE,
                            CompaniesActionType.EXIT_REGISTRY_COMPANIES_LIST_PAGE,
                            StoresActionType.EXIT_REGISTRY_STORES_LIST_PAGE,
                            ReportsActionType.EXIT_REPORTS_PAGE,
                        ),
                    ),
                );
            } else {
                return EMPTY;
            }
        }),
    );
};

export const fetchCashRegisterModelsListEpic: ShortListsEpic = (action$, state) => {
    return action$.pipe(
        ofType(CashBoxesActionType.OPEN_REGISTRY_CASHBOXES_LIST_PAGE, ServiceActionType.OPEN_SERVICE_PAGE),
        switchMap(() => {
            const { cashRegisterList } = state.value.shortListsReducer;

            if (!cashRegisterList || cashRegisterList.length === 0) {
                return from(shortListsClient.getCashRegisterModels()).pipe(
                    map((list) => fetchCashRegistersSuccessAction(list)),

                    catchError(() => NEVER),

                    takeUntil(
                        action$.ofType(
                            CashBoxesActionType.EXIT_REGISTRY_CASHBOXES_LIST_PAGE,
                            ServiceActionType.EXIT_SERVICE_PAGE,
                        ),
                    ),
                );
            } else {
                return EMPTY;
            }
        }),
    );
};

export const fetchOperatorsShortListEpic: ShortListsEpic = (action$, state) => {
    return action$.pipe(
        ofType(
            CashBoxesActionType.OPEN_REGISTRY_CASHBOXES_LIST_PAGE,
            CashRegistersActionType.OPEN_REGISTRY_CASH_REGISTERS_LIST_PAGE,
            DevicesActionType.OPEN_REGISTRY_DEVICES_LIST_PAGE,
            AdminUsersActionType.OPEN_ADMIN_USERS_LIST_PAGE,
            ServiceActionType.OPEN_SERVICE_PAGE,
        ),
        switchMap(() => {
            const { operatorsShortList } = state.value.shortListsReducer;
            const uiType = state.value.userReducer.authData ? state.value.userReducer.authData.uiType : null;

            if ((!operatorsShortList || operatorsShortList.length === 0) && uiType !== UiType.Operator) {
                return from(shortListsClient.getOperatorsList()).pipe(
                    map((list) => fetchOperatorsListSuccessAction(list)),

                    catchError(() => NEVER),

                    takeUntil(
                        action$.ofType(
                            CashBoxesActionType.EXIT_REGISTRY_CASHBOXES_LIST_PAGE,
                            CashRegistersActionType.EXIT_REGISTRY_CASH_REGISTERS_LIST_PAGE,
                            DevicesActionType.EXIT_REGISTRY_DEVICES_LIST_PAGE,
                            AdminUsersActionType.EXIT_ADMIN_USERS_LIST_PAGE,
                            ServiceActionType.EXIT_SERVICE_PAGE,
                        ),
                    ),
                );
            } else {
                return EMPTY;
            }
        }),
    );
};

export const fetchFirmwareVersionsListEpic: ShortListsEpic = (action$, state) => {
    return action$.pipe(
        ofType(
            CashBoxesActionType.OPEN_REGISTRY_CASHBOXES_LIST_PAGE,
            DevicesActionType.OPEN_REGISTRY_DEVICES_LIST_PAGE,
            ServiceActionType.OPEN_SERVICE_PAGE,
        ),
        switchMap(() => {
            const { firmwareVersionsList } = state.value.shortListsReducer;
            const uiType = state.value.userReducer.authData ? state.value.userReducer.authData.uiType : null;

            if (uiType !== UiType.Taxpayer && (!firmwareVersionsList || firmwareVersionsList.length === 0)) {
                return from(shortListsClient.getFirmwareVersions()).pipe(
                    map((list) => fetchFirmwareVersionsListSuccessAction(list)),

                    catchError(() => NEVER),

                    takeUntil(
                        action$.ofType(
                            CashBoxesActionType.EXIT_REGISTRY_CASHBOXES_LIST_PAGE,
                            DevicesActionType.EXIT_REGISTRY_DEVICES_LIST_PAGE,
                            ServiceActionType.EXIT_SERVICE_PAGE,
                        ),
                    ),
                );
            } else {
                return EMPTY;
            }
        }),
    );
};

export const fetchFiscalDriverVersionsListEpic: ShortListsEpic = (action$, state) => {
    return action$.pipe(
        ofType(CashBoxesActionType.OPEN_REGISTRY_CASHBOXES_LIST_PAGE, ServiceActionType.OPEN_SERVICE_PAGE),
        switchMap(() => {
            const { fiscalDriverVersionsList } = state.value.shortListsReducer;
            const uiType = state.value.userReducer.authData ? state.value.userReducer.authData.uiType : null;

            if (uiType !== UiType.Taxpayer && (!fiscalDriverVersionsList || fiscalDriverVersionsList.length === 0)) {
                return from(shortListsClient.getFiscalDriverVersions()).pipe(
                    map((list) => fetchFiscalDriverVersionListSuccessAction(list)),

                    catchError(() => NEVER),

                    takeUntil(
                        action$.ofType(
                            CashBoxesActionType.EXIT_REGISTRY_CASHBOXES_LIST_PAGE,
                            ServiceActionType.EXIT_SERVICE_PAGE,
                        ),
                    ),
                );
            } else {
                return EMPTY;
            }
        }),
    );
};

export const fetchTaxAuthoritiesListEpic: ShortListsEpic = (action$, state) => {
    return action$.pipe(
        ofType(
            CashBoxesActionType.OPEN_REGISTRY_CASHBOXES_LIST_PAGE,
            CompaniesActionType.OPEN_REGISTRY_COMPANIES_LIST_PAGE,
            StoresActionType.OPEN_REGISTRY_STORES_LIST_PAGE,
            SettingsUserInfoActionType.OPEN_SETTINGS_USER_INFO_PAGE,
            AdminUsersActionType.OPEN_ADMIN_USERS_LIST_PAGE,
        ),
        switchMap(() => {
            const { taxAuthorityList } = state.value.shortListsReducer;

            if (!taxAuthorityList || taxAuthorityList.length === 0) {
                return from(shortListsClient.getTaxAuthorities()).pipe(
                    map((list) => fetchTaxAuthoritiesListSuccessAction(list)),

                    catchError(() => NEVER),

                    takeUntil(
                        action$.ofType(
                            CashBoxesActionType.EXIT_REGISTRY_CASHBOXES_LIST_PAGE,
                            CompaniesActionType.EXIT_REGISTRY_COMPANIES_LIST_PAGE,
                            StoresActionType.EXIT_REGISTRY_STORES_LIST_PAGE,
                            SettingsUserInfoActionType.EXIT_SETTINGS_USER_INFO_PAGE,
                            AdminUsersActionType.EXIT_ADMIN_USERS_LIST_PAGE,
                        ),
                    ),
                );
            } else {
                return EMPTY;
            }
        }),
    );
};

export const fetchTaxationTypesListEpic: ShortListsEpic = (action$, state) => {
    return action$.pipe(
        ofType(
            CashBoxesActionType.OPEN_REGISTRY_CASHBOXES_LIST_PAGE,
            CompaniesActionType.OPEN_REGISTRY_COMPANIES_LIST_PAGE,
            StoresActionType.OPEN_REGISTRY_STORES_LIST_PAGE,
        ),
        switchMap(() => {
            const { taxationTypesList } = state.value.shortListsReducer;

            if (!taxationTypesList || taxationTypesList.length === 0) {
                return from(shortListsClient.getTaxationTypes()).pipe(
                    map((list) => fetchTaxationTypesListSuccessAction(list)),

                    catchError(() => NEVER),

                    takeUntil(
                        action$.ofType(
                            CashBoxesActionType.EXIT_REGISTRY_CASHBOXES_LIST_PAGE,
                            CompaniesActionType.EXIT_REGISTRY_COMPANIES_LIST_PAGE,
                            StoresActionType.EXIT_REGISTRY_STORES_LIST_PAGE,
                        ),
                    ),
                );
            } else {
                return EMPTY;
            }
        }),
    );
};

export const fetchCitiesListEpic: ShortListsEpic = (action$, state) => {
    return action$.pipe(
        ofType(StoresMapActionType.OPEN_STORES_MAP_PAGE),
        switchMap(() => {
            const { cities } = state.value.shortListsReducer;

            if (!cities || cities.length === 0) {
                return from(shortListsClient.getCities()).pipe(
                    map((cities) => fetchCitiesListSuccessActon(cities)),

                    catchError(() => NEVER),

                    takeUntil(action$.ofType(StoresMapActionType.EXIT_STORES_MAP_PAGE)),
                );
            } else {
                return EMPTY;
            }
        }),
    );
};

export const fetchRegionsListEpic: ShortListsEpic = (action$, state) => {
    return action$.pipe(
        ofType(CashBoxesActionType.OPEN_REGISTRY_CASHBOXES_LIST_PAGE, StoresActionType.OPEN_REGISTRY_STORES_LIST_PAGE),
        switchMap(() => {
            const { regions } = state.value.shortListsReducer;

            if (!regions || regions.length === 0) {
                return from(shortListsClient.getRegions()).pipe(
                    map((list) => fetchRegionsListSuccessActon(list)),

                    catchError(() => NEVER),

                    takeUntil(
                        action$.ofType(
                            CashBoxesActionType.EXIT_REGISTRY_CASHBOXES_LIST_PAGE,
                            StoresActionType.EXIT_REGISTRY_STORES_LIST_PAGE,
                        ),
                    ),
                );
            } else {
                return EMPTY;
            }
        }),
    );
};

export const fetchUsersListEpic: ShortListsEpic = (action$, state) => {
    return action$.pipe(
        ofType(
            AlertsActionType.OPEN_ALERTS_PAGE,
            AlertsSettingsActionType.OPEN_ALERTS_SETTINGS_PAGE,
            AlertsDetailsActionType.OPEN_ALERTS_DETAILS_PAGE,
        ),
        switchMap(() => {
            const { users } = state.value.shortListsReducer;

            if (!users || users.length === 0) {
                return from(shortListsClient.getUsersList()).pipe(
                    map((users) => fetchUsersListSuccessAction(users)),

                    catchError(() => NEVER),

                    takeUntil(
                        action$.ofType(
                            AlertsActionType.EXIT_ALERTS_PAGE,
                            AlertsSettingsActionType.EXIT_ALERTS_SETTINGS_PAGE,
                            AlertsDetailsActionType.EXIT_ALERTS_DETAILS_PAGE,
                        ),
                    ),
                );
            } else {
                return EMPTY;
            }
        }),
    );
};

export const fetchMainActivitiesListEpic: ShortListsEpic = (action$, state) => {
    return action$.pipe(
        ofType(CashBoxesActionType.OPEN_REGISTRY_CASHBOXES_LIST_PAGE, StoresActionType.OPEN_REGISTRY_STORES_LIST_PAGE),
        switchMap(() => {
            const { mainActivitiesList } = state.value.shortListsReducer;
            const uiType = state.value.userReducer.authData ? state.value.userReducer.authData.uiType : null;

            if (uiType !== UiType.Taxpayer && (!mainActivitiesList || mainActivitiesList.length === 0)) {
                return from(shortListsClient.getListOfMainActivities()).pipe(
                    map((list) => fetchMainActivitiesListSuccessActon(list)),

                    catchError(() => NEVER),

                    takeUntil(
                        action$.ofType(
                            CashBoxesActionType.EXIT_REGISTRY_CASHBOXES_LIST_PAGE,
                            StoresActionType.EXIT_REGISTRY_STORES_LIST_PAGE,
                        ),
                    ),
                );
            } else {
                return EMPTY;
            }
        }),
    );
};

export const fetchMiddleActivitiesListEpic: ShortListsEpic = (action$, state) => {
    return action$.pipe(
        ofType(CashBoxesActionType.OPEN_REGISTRY_CASHBOXES_LIST_PAGE, StoresActionType.OPEN_REGISTRY_STORES_LIST_PAGE),
        switchMap(() => {
            const { middleActivitiesList } = state.value.shortListsReducer;
            const uiType = state.value.userReducer.authData ? state.value.userReducer.authData.uiType : null;

            if (uiType !== UiType.Taxpayer && (!middleActivitiesList || middleActivitiesList.length === 0)) {
                return from(shortListsClient.getListOfMiddleActivities()).pipe(
                    map((list) => fetchMiddleActivitiesListSuccessActon(list)),

                    catchError(() => NEVER),

                    takeUntil(
                        action$.ofType(
                            CashBoxesActionType.EXIT_REGISTRY_CASHBOXES_LIST_PAGE,
                            StoresActionType.EXIT_REGISTRY_STORES_LIST_PAGE,
                        ),
                    ),
                );
            } else {
                return EMPTY;
            }
        }),
    );
};

export const fetchLowerActivitiesListEpic: ShortListsEpic = (action$, state) => {
    return action$.pipe(
        ofType(CashBoxesActionType.OPEN_REGISTRY_CASHBOXES_LIST_PAGE, StoresActionType.OPEN_REGISTRY_STORES_LIST_PAGE),
        switchMap(() => {
            const { lowerActivitiesList } = state.value.shortListsReducer;
            const uiType = state.value.userReducer.authData ? state.value.userReducer.authData.uiType : null;

            if (uiType !== UiType.Taxpayer && (!lowerActivitiesList || lowerActivitiesList.length === 0)) {
                return from(shortListsClient.getListOfLowerActivities()).pipe(
                    map((list) => fetchLowerActivitiesListSuccessActon(list)),

                    catchError(() => NEVER),

                    takeUntil(
                        action$.ofType(
                            CashBoxesActionType.EXIT_REGISTRY_CASHBOXES_LIST_PAGE,
                            StoresActionType.EXIT_REGISTRY_STORES_LIST_PAGE,
                        ),
                    ),
                );
            } else {
                return EMPTY;
            }
        }),
    );
};

export const fetchAiActivitiesListEpic: ShortListsEpic = (action$, state) => {
    return action$.pipe(
        ofType(
            CashBoxesActionType.OPEN_REGISTRY_CASHBOXES_LIST_PAGE,
            StoresActionType.OPEN_REGISTRY_STORES_LIST_PAGE,
            CompaniesActionType.OPEN_REGISTRY_COMPANIES_LIST_PAGE,
        ),
        switchMap(() => {
            const { aiActivitiesList } = state.value.shortListsReducer;
            const uiType = state.value.userReducer.authData ? state.value.userReducer.authData.uiType : null;

            if (
                uiType !== UiType.Taxpayer &&
                uiType !== UiType.Operator &&
                (!aiActivitiesList || aiActivitiesList.length === 0)
            ) {
                return from(shortListsClient.getListOfAiActivities()).pipe(
                    map((list) => fetchAiActivitiesListSuccessActon(list)),

                    catchError(() => NEVER),

                    takeUntil(
                        action$.ofType(
                            CashBoxesActionType.EXIT_REGISTRY_CASHBOXES_LIST_PAGE,
                            StoresActionType.EXIT_REGISTRY_STORES_LIST_PAGE,
                            CompaniesActionType.EXIT_REGISTRY_COMPANIES_LIST_PAGE,
                        ),
                    ),
                );
            } else {
                return EMPTY;
            }
        }),
    );
};
